import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useDispatch } from "react-redux";

import Box from "@/main/components/shared/box";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputAutoCompleteCheckControlled from "@/main/components/shared/inputAutoCompleteCheck/inputAutoCompleteCheckControlled";

import useFormQueryParams from "@/main/hooks/useformQueryParams";

import { getCompany, resetReducerCompany, useCompanyReducer } from "@/main/store/company";
import { getCompanyType, useCompanyTypeReducer } from "@/main/store/companyType";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getSystemApplication, useSystemApplicationReducer } from "@/main/store/systemApplication";

import { KeyValue } from "@/main/models/common";
import { CompanyFilterRequest } from "@/main/models/request";
import { CompanyTypeEnum } from "@/main/services/enum/CompanyTypeEnum";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import { StatusUserGroupEnum } from "@/main/services/enum/StatusUserGroupEnum";

import { getOptionsStatus } from "@/main/services/data/data";
import {
	getDefaultCountryOptionDrop,
	getDefaultOptionDrop,
	getTranslateByKey,
	isValueQueryParams,
} from "@/main/services/utils";

import { isNullUndefined } from "@/main/services/reducerUtils";
import { getCulture, useCultureReducer } from "@/main/store/culture";
import { getFileType, useFileTypeReducer } from "@/main/store/fileType";
import { useWatch } from "react-hook-form";
import {
	INITIAL_TERMS_OF_USE_FORM,
	ITermsOfUseFilterForm,
	ITermsOfUseFilterFormOutput,
	termsOfUseFilterSchema,
} from "../../termsOfUseUtils";
import * as S from "./styles";

const ID_BASE_COMPONENTS = "id-searchTermsOfUse";

type IFiltersTermsOfUse = {
	onSubmit: (data: ITermsOfUseFilterFormOutput) => void;
	onClear: () => void;
	showFilters: boolean;
};

export default function FiltersTermsOfUse({ onSubmit, onClear, showFilters }: Readonly<IFiltersTermsOfUse>) {
	const dispatch = useDispatch();

	const {
		countriesUser: { data: countries, isLoading: isLoadingCountries },
	} = useCountryReducer();

	const {
		companyType: { data: companyTypes, isLoading: isLoadingCompanyTypes },
	} = useCompanyTypeReducer();

	const {
		company: { data: companies, isLoading: isLoadingCompanies },
	} = useCompanyReducer();

	const {
		systemApplication: { data: systemApplications, isLoading: isLoadingSystemApplications },
	} = useSystemApplicationReducer();

	const {
		fileType: { data: fileTypes, isLoading: isLoadingFileTypes },
	} = useFileTypeReducer();

	const {
		culture: { data: cultures, isLoading: isLoadingCultures },
	} = useCultureReducer();

	const statusOptions = getOptionsStatus();
	const defaultStatusOption = statusOptions.find((option) => Number(option.key) === StatusUserGroupEnum.Active) || null;

	const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countries);
	const optionDefaultCompanyTypeDrop = getDefaultOptionDrop(companyTypes);

	const {
		control,
		setValue,
		customHandleSubmit,
		reset,
		getValues,
		formState: { errors },
	} = useFormQueryParams<ITermsOfUseFilterForm, ITermsOfUseFilterFormOutput>({
		mode: "onChange",
		defaultValues: INITIAL_TERMS_OF_USE_FORM,
		onSubmit: onSubmit,
		resolver: zodResolver(termsOfUseFilterSchema),
		mapDefaultValues: {
			country: countries,
			companyType: companyTypes,
			company: companies,
			systemApplication: systemApplications,
			fileType: fileTypes,
		} as any,
	});

	const isDisabledCompanyTypeDrop = !companyTypes || companyTypes.length === 1;

	const isDisabledFileTypeDrop = !fileTypes;

	const isDisabledCompanyDrop = () => getValues().companyType === null || !companies || companies.length === 0;

	const watchedCountry = useWatch({ control, name: "country" });
	const watchedCompanyType = useWatch({ control, name: "companyType" });
	const watchedSystemApplication = useWatch({
		control,
		name: "systemApplication",
	});

	useEffect(() => {
		if (!isValueQueryParams(watchedCountry)) return;
		if (!isValueQueryParams(watchedCompanyType)) return;
		callCompanyApiDomain(watchedCountry, watchedCompanyType);
	}, [watchedCountry, watchedCompanyType]);

	useEffect(() => {
		if (!isValueQueryParams(watchedSystemApplication) || isNullUndefined(watchedSystemApplication)) return;
		callFileTypeApiDomain(watchedSystemApplication);
	}, [watchedSystemApplication]);

	useEffect(() => {
		dispatch(getCountriesUser());
		dispatch(getCompanyType({ screenCompanytype: true }));
		dispatch(getSystemApplication());
	}, []);

	useEffect(() => {
		if (!optionDefaultCountryDrop) return;

		callCultureApiDomain(optionDefaultCountryDrop);
		setValue("country", optionDefaultCountryDrop);

		if (optionDefaultCompanyTypeDrop) {
			callCompanyApiDomain(optionDefaultCountryDrop, optionDefaultCompanyTypeDrop);
		}
	}, [optionDefaultCountryDrop?.key, optionDefaultCompanyTypeDrop?.key]);

	function callFileTypeApiDomain(systemApplicationSelected: KeyValue) {
		dispatch(getFileType(Number(systemApplicationSelected.key)));
	}

	function callCultureApiDomain(countrySelected: KeyValue) {
		dispatch(getCulture({ guidCountry: String(countrySelected.key) }));
	}

	function callCompanyApiDomain(countrySelected: KeyValue | null, companyTypeSelected: KeyValue | null) {
		if (!isValueQueryParams(countrySelected) || !isValueQueryParams(companyTypeSelected)) {
			setValue("company", []);
		}

		dispatch(resetReducerCompany());

		if (countrySelected !== null) {
			const request: CompanyFilterRequest = {
				guidCountry: String(countrySelected.key),
				type: HeadQuarterBranchFilterEnum.All,
				seeOnlyCompaniesWithAnAgreement: companyTypeSelected?.key != CompanyTypeEnum.Insurer,
			};
			if (companyTypeSelected) {
				request.idCompanyType = String(companyTypeSelected.key);
				dispatch(getCompany(request));
			}
		}
	}

	function handleClear() {
		reset(INITIAL_TERMS_OF_USE_FORM);

		setValue("country", optionDefaultCountryDrop);

		if (optionDefaultCountryDrop) {
			callCultureApiDomain(optionDefaultCountryDrop);
			if (optionDefaultCompanyTypeDrop) {
				setValue("companyType", optionDefaultCompanyTypeDrop);
				callCompanyApiDomain(optionDefaultCountryDrop, optionDefaultCompanyTypeDrop);
			}
		}

		if (defaultStatusOption) {
			setValue("statusRegister", defaultStatusOption);
		}

		onClear();
	}

	return (
		<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
			<form onSubmit={customHandleSubmit} noValidate>
				<S.RowInputs>
					<KogAutoCompleteControlled
						required
						id={`${ID_BASE_COMPONENTS}-select-country`}
						label={getTranslateByKey("information.country")}
						control={control}
						options={countries}
						isLoading={isLoadingCountries}
						nameCustom="country"
						rulesCustom={{ required: true }}
						errorMessageCustom={errors.country?.message ?? ""}
						hasErrorCustom={!!errors.country}
						onChangeCustom={(optionSelected: KeyValue | null) => {
							const companyTypeSelected = getValues().companyType;
							if (companyTypeSelected) {
								callCompanyApiDomain(optionSelected, companyTypeSelected);
							}

							if (optionSelected) callCultureApiDomain(optionSelected);
						}}
						valueDefaultKey={optionDefaultCountryDrop?.key}
						setValueCustom={setValue}
						disabled={optionDefaultCountryDrop !== null}
					/>

					<KogAutoCompleteControlled
						required
						id={`${ID_BASE_COMPONENTS}-select-companyType`}
						label={getTranslateByKey("information.companyType")}
						control={control}
						options={companyTypes}
						isLoading={isLoadingCompanyTypes}
						nameCustom="companyType"
						rulesCustom={{ required: true }}
						errorMessageCustom={errors.companyType?.message ?? ""}
						setValueCustom={setValue}
						hasErrorCustom={!!errors.companyType}
						isDefaultValueUnique
						disabled={isDisabledCompanyTypeDrop}
						onChangeCustom={(optionSelected: KeyValue | null) => {
							callCompanyApiDomain(getValues().country, optionSelected);
						}}
					/>

					<KogInputAutoCompleteCheckControlled
						name="company"
						control={control}
						disableVirtualization
						label={getTranslateByKey("information.company")}
						options={companies || []}
						customInputProps={{
							id: `${ID_BASE_COMPONENTS}-select-company`,
						}}
						selectAllLabel={getTranslateByKey("information.selectAll")}
						isLoading={isLoadingCompanies}
						isDefaultValueUnique={companies === null || companies.length === 1}
						onChangeCustom={() => {}}
						disabled={isDisabledCompanyDrop()}
					/>

					<KogAutoCompleteControlled
						required
						id={`${ID_BASE_COMPONENTS}-select-system`}
						label={getTranslateByKey("information.systemApplication")}
						control={control}
						nameCustom="systemApplication"
						rulesCustom={{ required: true }}
						errorMessageCustom={errors.systemApplication?.message ?? ""}
						hasErrorCustom={!!errors.systemApplication}
						options={systemApplications}
						isLoading={isLoadingSystemApplications}
						onChangeCustom={(optionSelected: KeyValue | null) => {
							if (!isNullUndefined(optionSelected)) {
								callFileTypeApiDomain(optionSelected);
							}
						}}
						setValueCustom={setValue}
					/>

					<KogAutoCompleteControlled
						id={`${ID_BASE_COMPONENTS}-select-term`}
						label={getTranslateByKey("information.term")}
						control={control}
						nameCustom="fileType"
						rulesCustom={{ required: false }}
						errorMessageCustom=""
						hasErrorCustom={false}
						disabled={isDisabledFileTypeDrop}
						options={fileTypes}
						isLoading={isLoadingFileTypes}
						onChangeCustom={() => {}}
						setValueCustom={setValue}
					/>

					<KogAutoCompleteControlled
						required
						id={`${ID_BASE_COMPONENTS}-select-culture`}
						label={getTranslateByKey("information.userLanguage")}
						control={control}
						options={cultures}
						isLoading={isLoadingCultures}
						nameCustom="culture"
						rulesCustom={{ required: true }}
						errorMessageCustom={errors.culture?.message ?? ""}
						setValueCustom={setValue}
						hasErrorCustom={!!errors.culture}
						isDefaultValueUnique
						onChangeCustom={() => {}}
					/>

					<KogAutoCompleteControlled
						required
						id={`${ID_BASE_COMPONENTS}-select-statusRegister`}
						label={getTranslateByKey("information.status")}
						nameCustom="statusRegister"
						errorMessageCustom={errors.statusRegister?.message ?? ""}
						hasErrorCustom={!!errors.statusRegister}
						control={control}
						setValueCustom={setValue}
						options={statusOptions}
						rulesCustom={{ required: true }}
						valueDefaultKey={defaultStatusOption?.key}
						onChangeCustom={() => {}}
					/>
				</S.RowInputs>

				<S.AreaButtons>
					<SecondaryButton id={`${ID_BASE_COMPONENTS}-btn-search`} type="submit">
						{getTranslateByKey("information.search")}
					</SecondaryButton>

					<SecondaryButton id={`${ID_BASE_COMPONENTS}-btn-clear`} onClick={handleClear}>
						{getTranslateByKey("information.clear")}
					</SecondaryButton>
				</S.AreaButtons>
			</form>
		</Box>
	);
}
